import React, { createContext, useMemo, useContext, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { reverse, sortBy } from 'lodash';

import { useAuth } from '../../authentication';
import {
  useGetConversationsQuery,
  useCreateConversationMutation,
  useGetConversationQuery,
  useUpdateConversationMutation,
  useDeleteConversationMutation
} from '../hooks';

import { useOrganizations } from '../../organization/providers';

import { Conversation } from '../models';

const ConversationsContext = createContext();

const ConversationsProvider = ({ children = null, type = null }) => {
  // #region State
  const [currentConversation, setCurrentConversation] = useState(null);
  const [groupedConversations, setGroupedConverations] = useState({});
  const [conversations, setConversations] = useState([]);

  // #region Hooks
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const { accessToken } = useAuth();
  const { currentOrganization } = useOrganizations();
  const conversationsQuery = useGetConversationsQuery(accessToken, currentOrganization?.id, type);
  const conversationQuery = useGetConversationQuery(
    accessToken,
    conversationId,
    currentOrganization?.id
  );
  const createConversationMutation = useCreateConversationMutation(accessToken);
  const updateConversationMutation = useUpdateConversationMutation(accessToken);
  const deleteConversationMutation = useDeleteConversationMutation(accessToken);
  // #endregion

  // #region Callbacks and Functions
  const touchConversation = useCallback(
    (id, params = {}, refetchConversation = true) => {
      const queryKey = ['chat', 'conversations', currentOrganization.id, type];

      queryClient.setQueryData(queryKey, (oldData) => {
        // "Cannot read properties of undefined (reading 'pages')"
        // —happens because the updater function in touchConversation
        // assumes that the existing data (referred to as oldData) always
        // has a pages property. However, if the conversation list data
        // hasn't been fetched or cached yet.
        if (!oldData || !oldData.pages) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            data: reverse(
              sortBy(
                page.data.map((conversation) => {
                  if (conversation.id === id) {
                    return { ...conversation, ...params, updatedAt: new Date().toISOString() };
                  }
                  return conversation;
                }),
                'updatedAt'
              )
            )
          }))
        };
      });

      if (refetchConversation && currentConversation && currentConversation.id) {
        const conversationQueryKey = ['chat', 'conversations', currentConversation.id];
        queryClient.setQueryData(conversationQueryKey, (oldData) => ({
          ...oldData,
          ...params
        }));
      }
    },
    [currentOrganization, currentConversation?.id, type, queryClient]
  );

  const groupConversations = useCallback(
    (_conversations) => {
      const updatedGroups = {};

      // Get current date and define date boundaries
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      const startOfWeek = new Date(today);
      // Adjust if your week starts on Monday
      startOfWeek.setDate(today.getDate() - today.getDay());

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      _conversations.forEach((conversation) => {
        const updatedAt = new Date(conversation.updatedAt);

        if (updatedAt >= today) {
          updatedGroups.Today = updatedGroups.Today || [];
          updatedGroups.Today.push(conversation);
        } else if (updatedAt >= yesterday && updatedAt < today) {
          updatedGroups.Yesterday = updatedGroups.Yesterday || [];
          updatedGroups.Yesterday.push(conversation);
        } else if (updatedAt >= startOfWeek && updatedAt < yesterday) {
          updatedGroups['This week'] = updatedGroups['This week'] || [];
          updatedGroups['This week'].push(conversation);
        } else if (updatedAt >= startOfMonth && updatedAt < startOfWeek) {
          updatedGroups['This month'] = updatedGroups['This month'] || [];
          updatedGroups['This month'].push(conversation);
        } else {
          const monthYear = updatedAt.toLocaleString('default', {
            month: 'long',
            year: 'numeric'
          });
          updatedGroups[monthYear] = updatedGroups[monthYear] || [];
          updatedGroups[monthYear].push(conversation);
        }
      });

      setGroupedConverations(updatedGroups);
    },
    [setGroupedConverations]
  );

  const openNextConversation = useCallback(
    (id, openNew = false) => {
      if (openNew) {
        return navigate('/chats/new');
      }

      if (currentConversation?.id === id) {
        if (conversations.length > 1) {
          if (conversations[0].id === id) {
            return navigate(`/chats/${conversations[1].id}`);
          }
          return navigate(`/chats/${conversations[0].id}`);
        }
      }

      return navigate('/chats/new');
    },
    [conversations, currentConversation?.id, navigate]
  );

  const createConversation = useCallback(
    async (title, assetQuestionId = null) => {
      const queryKey = ['chat', 'conversations', currentOrganization.id, type];

      // Optimistic update
      queryClient.setQueryData(queryKey, (oldData) => ({
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          data: [
            { title, updatedAt: new Date().toISOString(), createdAt: new Date().toISOString() },
            ...page.data
          ]
        }))
      }));

      const { data: conversation } = await createConversationMutation.mutateAsync({
        data: { title, type, assetQuestionId },
        organizationId: currentOrganization?.id
      });

      // Update cache with actual conversation data
      queryClient.setQueryData(queryKey, (oldData) => ({
        ...oldData,
        pages: oldData.pages.map((page) => ({
          ...page,
          data: page.data.map((c) => (!c.id ? conversation : c))
        }))
      }));

      return conversation;
    },
    [createConversationMutation, currentOrganization?.id, queryClient, type]
  );

  const updateConversation = useCallback(
    async (data, id = null) => {
      await updateConversationMutation.mutateAsync({
        data,
        conversationId: id ?? currentConversation.id,
        organizationId: currentOrganization?.id
      });
      const refetchConversation = id === currentConversation?.id;
      touchConversation(id ?? currentConversation?.id, data, refetchConversation);
    },
    [updateConversationMutation, currentOrganization?.id, currentConversation?.id]
  );

  const deleteConversation = useCallback(
    async (id, shared = false) => {
      const queryKey = ['chat', 'conversations', currentOrganization.id, type];
      const previousState = queryClient.getQueryData(queryKey);

      try {
        // Optimistically update the cache
        queryClient.setQueryData(queryKey, (oldData) => {
          if (!oldData || !oldData.pages) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.filter((conversation) => conversation.id !== id)
            }))
          };
        });

        if (!shared) {
          await deleteConversationMutation.mutateAsync({
            conversationId: id,
            organizationId: currentOrganization?.id
          });
        }
      } catch (err) {
        // Rollback to previous state on error
        queryClient.setQueryData(queryKey, previousState);
      }
    },
    [
      deleteConversationMutation,
      currentOrganization,
      currentConversation?.id,
      conversations,
      navigate,
      queryClient,
      type
    ]
  );
  // #endregion

  // #region Effects
  useEffect(() => {
    if (conversationQuery.data) {
      setCurrentConversation(conversationQuery.data);
    } else if (!conversationId) {
      setCurrentConversation(null);
    }
  }, [conversationQuery.data, conversationId]);

  useEffect(() => {
    if (!conversationsQuery.data) return;

    const allConversations = conversationsQuery.data.pages.flatMap((page) =>
      page.data.map((conversation) => new Conversation(conversation))
    );

    groupConversations(allConversations);
    setConversations(allConversations);
  }, [conversationsQuery.data]);
  // #endregion

  const value = useMemo(
    () => ({
      groupedConversations,
      conversations,
      fetchNextConversationsPage: conversationsQuery.isRefetching
        ? null
        : conversationsQuery.fetchNextPage,
      refetch: conversationsQuery.refetch,
      createConversation,
      currentConversation: new Conversation(currentConversation),
      updateConversation,
      deleteConversation,
      touchConversation,
      openNextConversation
    }),
    [
      conversationsQuery.data,
      currentConversation,
      groupedConversations,
      touchConversation,
      createConversation,
      updateConversation,
      deleteConversation,
      openNextConversation,
      conversations
    ]
  );

  if (conversationsQuery.isLoading) return null;

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
};

ConversationsProvider.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string
};

const useConversations = () => useContext(ConversationsContext);

export { ConversationsProvider, ConversationsContext, useConversations };
